import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { useId, type ReactElement, cloneElement } from 'react';

/** The shape of the MUI input this wraps, as far as Field needs to know. */
interface WrappedInputProps {
  id?: string;
  label?: string;
  error?: boolean;
  slotProps?: { htmlInput?: Record<string, unknown> };
}

interface FieldProps {
  label: string;
  /** Shown under the control when validation fails. */
  error?: string;
  /** Extra guidance shown under the control. */
  hint?: string;
  /** A MUI input, select or textarea. Receives the label and aria attributes. */
  children: ReactElement<WrappedInputProps>;
}

/**
 * Wraps one form control with its label, hint and error message, and wires up
 * the accessibility attributes that connect them.
 *
 * Doing this in one component is what makes the WCAG 2.1 AA requirement
 * testable: every field in the portal then announces its own error to a screen
 * reader, instead of showing red text that only sighted users notice.
 *
 * Note that the aria attributes go through `slotProps.htmlInput`. Props spread
 * directly onto a MUI input land on its outer wrapper, not on the `<input>`
 * itself, where a screen reader would never see them.
 */
export function Field({ label, error, hint, children }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy = [hint ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(' ');

  const childProps = children.props;

  return (
    <FormControl error={Boolean(error)} fullWidth>
      {cloneElement(children, {
        id,
        label,
        error: Boolean(error),
        slotProps: {
          ...childProps.slotProps,
          htmlInput: {
            'aria-describedby': describedBy || undefined,
            ...childProps.slotProps?.htmlInput,
          },
        },
      })}

      {hint ? <FormHelperText id={hintId}>{hint}</FormHelperText> : null}

      {error ? (
        // role="alert" makes a screen reader announce the message as soon as it
        // appears, rather than only when the person happens to reach the field.
        <FormHelperText id={errorId} role="alert" sx={{ fontWeight: 500 }}>
          {error}
        </FormHelperText>
      ) : null}
    </FormControl>
  );
}
